import {createAsyncThunk, createEntityAdapter, createSlice} from '@reduxjs/toolkit'
import {ViewOrder} from '../../models/OrderState'
import * as model from './helper'
import {RootState} from 'setup'
import find from 'lodash/find'
import orderEditService from 'app/services/OrderEditService/OrderEditService'
import {showMessage} from 'app/redux/messageSlice'
import {compliantStatusTypes, deliveryTypes, eventNames} from 'app/constant'
import shippingComplianceService from '../../services/ShippingComplianceService'
import {getEditOrder} from './actions/edit'
import * as edit from './actions/edit'
import {sellingPrice} from '../../helper'
import _, {filter} from 'lodash'
import EditOrderState, {
  isOrderPaymentFailed,
  Order,
  orderEditInitialState,
} from '../../models/EditOrderState'
import eventPublishService from 'app/services/EventPublishService/EventPublishService'

export const createEditOrder = createAsyncThunk(
  'order/create_edit',
  async (id: string, {dispatch}) => {
    try {
      dispatch(setIsFetching(true))
      const data = await orderEditService.createOrderEdit(id)
      // To set order address which is used while creating order
      const orderAddressProfiles = data.order?.orderAddressProfiles
      const shippingAddress = model.mapToAddress(
        orderAddressProfiles?.nodes,
        data.shippingAddress,
        'shipping'
      )
      const billingAddress = model.mapToAddress(
        orderAddressProfiles?.nodes,
        data.billingAddress,
        'billing'
      )
      const {orderItems, ...order} = data
      dispatch(
        setEditOrder({
          ...order,
          email: data.order?.email,
          phone: data.order?.phone,
          shippingAddress,
          billingAddress,
          orderAddressProfiles,
          isCompliant: true,
          note: null,
        })
      )
      return model.mapToEditOrderItemModel(orderItems.nodes)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setIsFetching(false))
    }
  }
)

export const checkCompliance = createAsyncThunk(
  'order/check-compliance',
  async (_, {dispatch, rejectWithValue}) => {
    try {
      dispatch(setCompliantFetch(true))
      await dispatch(updateEditOrderWithItems())
      const {order} = await dispatch(getEditOrder())
      const result = await shippingComplianceService.checkOrderEditCompliance({
        orderId: order.id,
        orderEditId: order.orderEditId,
      })
      if (result && result.complianceStatus === compliantStatusTypes.COMPLIANT) {
        const {
          salesTax: {totalTax, crv},
        } = result
        dispatch(setShippingComplianceAndTax({totalTax, crv, isCompliant: true}))
      } else {
        dispatch(setShippingComplianceAndTax({totalTax: 0, crv: 0, isCompliant: false}))
      }
      return result
    } catch (error) {
      dispatch(
        showMessage({
          text: 'Error occurred while processing your request',
          variant: 'error',
        })
      )
      return rejectWithValue(error)
    } finally {
      dispatch(setCompliantFetch(false))
      await dispatch(updateEditOrder())
    }
  }
)

const orderPublish = (order: Order, history: any, res: any) => async (dispatch: any) => {
  if (res && res.status === 'success') {
    await eventPublishService.publish({
      message: {id: order.orderEditId},
      name: eventNames.orderEdit,
    })
    dispatch(setIsDirty(false))
    dispatch(
      showMessage({
        text: 'Your order has been updated successfully',
        variant: 'success',
      })
    )
    history(`/orders/${order.id}/fulfillments/view`)
  } else {
    if (res && res.errors) {
      dispatch(setOrderErrors(res.errors))
    }
    dispatch(
      showMessage({
        text: 'Error occurred while processing your request',
        variant: 'error',
      })
    )
  }
}

const completeUnPaidOrder = (history: any, shippingRate?: any) => async (dispatch: any) => {
  try {
    dispatch(setIsSubmitting(true))
    if (shippingRate) {
      await dispatch(setShippingRate(shippingRate))
    }
    const {payload: result} = await dispatch(checkCompliance())
    if (result && result.complianceStatus === compliantStatusTypes.COMPLIANT) {
      const {order}: EditOrderState = await dispatch(getEditOrder())
      const res = await orderEditService.completeEditOrder(
        order,
        order.orderDeliveryMethod === deliveryTypes.ship
      )
      dispatch(orderPublish(order, history, res))
    } else {
      dispatch(
        showMessage({
          text: 'Error occurred while processing your request',
          variant: 'error',
        })
      )
    }
  } catch (err) {
    console.error(err)
    dispatch(
      showMessage({
        text: 'Error occurred while processing your request',
        variant: 'error',
      })
    )
  } finally {
    dispatch(setIsSubmitting(false))
  }
}

export const completeOrderEdit = (history: any, shippingRate?: any) => async (dispatch: any) => {
  const {order, viewOrder}: EditOrderState = await dispatch(getEditOrder())

  if (isOrderPaymentFailed(viewOrder.financialStatus)) {
    await dispatch(completeUnPaidOrder(history, shippingRate))
  } else {
    if (order.orderDeliveryMethod === deliveryTypes.ship) {
      await dispatch(completeOrderEditByShipping(history, shippingRate))
    } else {
      await dispatch(completeOrderEditByPickup(history))
    }
  }
}

export const completeOrderEditByShipping =
  (history: any, shippingRate?: any) => async (dispatch: any) => {
    try {
      dispatch(setIsSubmitting(true))
      if (shippingRate) {
        await dispatch(setShippingRate(shippingRate))
      }
      const {payload: result} = await dispatch(checkCompliance())
      if (result && result.complianceStatus === compliantStatusTypes.COMPLIANT) {
        const {order} = await dispatch(getEditOrder())
        const res = await orderEditService.completeEditOrderByShipping(order)
        dispatch(orderPublish(order, history, res))
      } else {
        dispatch(
          showMessage({
            text: 'Error occurred while processing your request',
            variant: 'error',
          })
        )
      }
    } catch (err) {
      console.error(err)
      dispatch(
        showMessage({
          text: 'Error occurred while processing your request',
          variant: 'error',
        })
      )
    } finally {
      dispatch(setIsSubmitting(false))
    }
  }

export const completeOrderEditByPickup = (history: any) => async (dispatch: any) => {
  try {
    dispatch(setIsSubmitting(true))
    const {payload: result} = await dispatch(checkCompliance())
    if (result && result.complianceStatus === compliantStatusTypes.COMPLIANT) {
      const {order} = await dispatch(getEditOrder())
      const res = await orderEditService.completeEditOrderByPickup(order)
      dispatch(orderPublish(order, history, res))
    } else {
      dispatch(
        showMessage({
          text: 'Error occurred while processing your request',
          variant: 'error',
        })
      )
    }
  } catch (err) {
    console.error(err)
    dispatch(
      showMessage({
        text: 'Error occurred while processing your request',
        variant: 'error',
      })
    )
  } finally {
    dispatch(setIsSubmitting(false))
  }
}

export const updateEditOrderSummary =
  (orderItems: any[]) => async (dispatch: any, getState: any) => {
    let subtotalPrice: any = model.calculateSubTotalPrice(orderItems)
    if (orderItems.length === 0) {
      subtotalPrice = 0
    }
    await dispatch(setOrderSubtotal(subtotalPrice))
  }

export const updateOrderEditItems = async (order: any, lineItems: any[], deletedItems: any[]) => {
  const orderEdit = model.mapToUpdateOrderEdit(order)
  const deletedIds = model.mapDeleteOrderItemModel(lineItems, deletedItems)
  const orderItems = model.mapToOrderEditItemsModel(lineItems)
  return await orderEditService.updateOrderEditItems({
    deletedIds,
    orderItems,
    orderEdit,
  })
}

export const updateEditOrderTaxAndAutoDiscountRemoved = async (param: any) => {
  try {
    await orderEditService.updateEditOrderTaxAndAutoDiscountRemoved(param)
  } catch (err) {
    console.error(err)
  }
}

export const updateEditOrderWithItems = () => async (dispatch: any, getState: any) => {
  const {order, deletedItems} = await dispatch(getEditOrder())
  const lineItems = selectEditOrderItems(getState())
  const updatedItems = await updateOrderEditItems(order, lineItems, deletedItems)
  const filterNewItems = mapToUpdateLineItemsIds(lineItems, updatedItems)
  dispatch(updateManyOrderItems(filterNewItems))
}

export const applyDiscountCode = (discount: any) => async (dispatch: any) => {
  const discounts = await orderEditService.applyDiscountCode(discount)
  dispatch(setOrderEditDiscounts(discounts.discounts))
  return discounts
}

export const applyWineClubDiscount =
  (orderEditId: any, lineItems: any[]) => async (dispatch: any) => {
    const {viewOrder}: EditOrderState = await dispatch(getEditOrder())
    const response = await orderEditService.applyWineClubDiscount({
      orderEditId,
      customerId: viewOrder.customerId,
      lineItems: mapToLineItem(lineItems),
    })
    dispatch(setOrderEditDiscounts(response.discounts))
    return response
  }

export const validateOrderEditDiscount = () => async (dispatch: any, getState: any) => {
  try {
    const {
      orders: {orderEdit},
    } = getState()
    const {isDirtyDiscount} = orderEdit
    if (isDirtyDiscount) {
      dispatch(setIsValidatingDiscount(true))
      const lineItems = selectEditOrderItems(getState())
      const {viewOrder, order}: EditOrderState = await dispatch(getEditOrder())
      const data = await orderEditService.validateOrderEditDiscount({
        orderEditId: order.orderEditId,
        lineItems: mapToLineItem(lineItems),
        customerId: viewOrder.customerId,
      })
      dispatch(setOrderEditDiscounts(data.discounts))
      dispatch(setIsDirty(true))
    }
  } finally {
    dispatch(setIsValidatingDiscount(false))
  }
}

export const validateDiscountsAndRefetchTax = () => async (dispatch: any, getState: any) => {
  await dispatch(validateOrderEditDiscount())
  await dispatch(checkCompliance())
}

export const updateEditOrder = () => async (dispatch: any) => {
  const {order} = await dispatch(getEditOrder())
  const orderEdit = model.mapToUpdateOrderEdit(order)
  await orderEditService.updateEditOrder(orderEdit)
}

export const revalidateDiscountOnDeliveryMethod =
  () => async (dispatch: any, getState: boolean) => {
    const {order} = await dispatch(getEditOrder())
    await orderEditService.updateOrderEdit(order.id, {
      orderDeliveryMethod: order.orderDeliveryMethod,
    })
    dispatch(validateOrderEditDiscount())
  }

const checkExistingItems = (orderItem: any, isAdd?: boolean) => async (dispatch: any) => {
  if (orderItem.originalQty > 1) {
    if (isAdd) {
      dispatch(addManyProducts([{...orderItem, quantity: 1}]))
      dispatch(
        setDeletedItems({
          type: model.actionType.update,
          data: {...orderItem, quantity: orderItem.quantity - 1},
        })
      )
    } else if (orderItem.quantity > 1) {
      dispatch(
        setDeletedItems({
          type: model.actionType.update,
          data: {...orderItem},
        })
      )
    } else {
      dispatch(setDeletedItems({type: model.actionType.remove, data: orderItem.variantId}))
    }
  } else if (orderItem.originalQty === 1) {
    dispatch(setDeletedItems({type: model.actionType.remove, data: orderItem.variantId}))
  }
}

export const addNewOrderItems = (items: any[]) => async (dispatch: any) => {
  const {deletedItems} = await dispatch(getEditOrder())
  const existedItems = model.checkOrderItemIsAlreadyExist(items, deletedItems)
  if (existedItems.length) {
    dispatch(checkExistingItems(existedItems[0], true))
  } else {
    dispatch(setAddNewItems(items))
    dispatch(addManyProducts(items))
  }
  dispatch(validateOrderEditDiscount())
}

export const updateEditOrderItem =
  (orderItem: any, orderItems: any[]) => async (dispatch: any, getState: any) => {
    orderItem.quantity = parseInt(orderItem.quantity)
    if (_.isNaN(orderItem.quantity)) {
      orderItem.quantity = 0
    }
    const {
      orders: {orderEdit},
    } = getState()
    const {isDirtyShipping} = orderEdit
    const {deletedItems} = await dispatch(getEditOrder())
    const checkItem = find(orderItems, {variantId: orderItem.variantId})
    const deletedItem = find(deletedItems, {variantId: orderItem.variantId})
    let isAdd = null
    if (checkItem && checkItem.originalQty > orderItem.quantity) {
      isAdd = orderItem.quantity > checkItem.quantity
    }
    dispatch(
      updateOrderItem({
        id: orderItem.variantId,
        changes: {
          quantity: orderItem.quantity,
          isLocalUpdate: true,
        },
      })
    )
    if (deletedItem) {
      const quantity = parseInt(checkItem.originalQty) - orderItem.quantity
      if (quantity > 0) {
        dispatch(
          setDeletedItems({
            type: model.actionType.update,
            data: {...orderItem, quantity},
          })
        )
      } else {
        dispatch(setDeletedItems({type: model.actionType.remove, data: orderItem.variantId}))
      }
    } else if (isAdd === false) {
      dispatch(
        setDeletedItems([
          {...orderItem, quantity: parseInt(orderItem.originalQty) - orderItem.quantity},
        ])
      )
    }
    if (isDirtyShipping) {
      dispatch(resetShipping())
    }
    // dispatch(validateOrderEditDiscount())
  }

export const restoreDeletedItem = (orderItem: any, orderItems: any[]) => async (dispatch: any) => {
  dispatch(setDeletedItems({type: model.actionType.remove, data: orderItem.variantId}))
  const item = find(orderItems, {variantId: orderItem.variantId})

  if (!item) {
    dispatch(addOrderItem({...orderItem, quantity: orderItem.originalQty}))
  } else {
    dispatch(
      updateOrderItem({
        id: orderItem.variantId,
        changes: {
          quantity: orderItem.originalQty,
        },
      })
    )
  }
  dispatch(validateOrderEditDiscount())
}

export const removeEditOrderItem = (orderItem: any) => async (dispatch: any) => {
  if (orderItem.isLocalNew) {
    dispatch(setRemoveNewItem(orderItem.variantId))
    if (orderItem.id) {
      await orderEditService.deleteEditOrderItem(orderItem.id)
    }
  } else {
    const {deletedItems} = await dispatch(getEditOrder())
    const deletedItem = find(deletedItems, {variantId: orderItem.variantId})
    if (deletedItem) {
      dispatch(
        setDeletedItems({
          type: model.actionType.update,
          data: {
            ...orderItem,
            quantity: parseInt(orderItem.originalQty),
            totalPrice: parseInt(orderItem.originalQty) * sellingPrice(orderItem),
          },
        })
      )
    } else {
      dispatch(
        setDeletedItems([
          {
            ...orderItem,
            quantity: parseInt(orderItem.originalQty),
            totalPrice: parseInt(orderItem.originalQty) * sellingPrice(orderItem),
          },
        ])
      )
    }
  }
  dispatch(removeOrderItem(orderItem.variantId))
  dispatch(validateOrderEditDiscount())
}

export const getDiscountList = createAsyncThunk(
  'order/discountList',
  async (_, {dispatch}: any) => {
    try {
      const data = await orderEditService.getDiscountList()
      return data
    } catch (err) {
      dispatch(
        showMessage({
          text: 'Error occurred while processing your request',
          variant: 'error',
        })
      )
    }
  }
)

const orderAdapter = createEntityAdapter({selectId: (order: any) => order.variantId})
export const {selectAll: selectEditOrderItems} = orderAdapter.getSelectors(
  ({orders}: any) => orders.orderEdit
)

const orderEditSlice = createSlice({
  name: 'orderEdit',
  initialState: orderAdapter.getInitialState({...orderEditInitialState}),
  reducers: {
    setIsFetching: (state, action) => {
      state.isFetching = action.payload
    },
    setIsDirty: (state, action) => {
      state.isDirty = action.payload
    },
    setErrors: (state, action) => {
      state.errors = action.payload
    },
    setIsProductsEditMode: (state, action) => {
      state.isProductsEditMode = action.payload
    },
    setOrderErrors: (state, action) => {
      state.orderProcessErrors = action.payload
    },
    setDeletedItems: (state, action) => {
      edit.setDeleteItems(state, action.payload)
    },
    setIsSubmitting: (state, action) => {
      state.isSubmitting = action.payload
    },
    setIsCustomShipping: (state, action) => {
      state.isCustomRate = action.payload
    },
    setIsDirtyDiscount: (state, action) => {
      state.isDirtyDiscount = action.payload
    },
    setIsDirtyShipping: (state, action) => {
      state.isDirtyShipping = action.payload
    },
    setIsShippingAddressSame: (state, action) => {
      state.isShippingAddressSame = action.payload
    },
    addManyProducts: orderAdapter.addMany,
    addOrderItem: orderAdapter.addOne,
    updateOrderItem: orderAdapter.updateOne,
    removeOrderItem: orderAdapter.removeOne,
    updateManyOrderItems: orderAdapter.updateMany,
    setOrderItems: orderAdapter.setAll,
    setIsValidatingDiscount: (state, action) => edit.setIsValidatingDiscount(state, action),
    setEditOrder: (state, action) => edit.setEditOrder(state, action),
    setCompliantFetch: (state, action) => edit.setCompliantFetch(state, action),
    setOrderSubtotal: (state, action) => edit.setOrderSubtotal(state, action),
    setAddNewItems: (state, action) => edit.setAddNewItems(state, action),
    setRemoveNewItem: (state, action) => edit.setRemoveNewItem(state, action),
    setOrderDeliveryMethod: (state, action) => edit.setOrderDeliveryMethod(state, action),
    setOrderNote: (state, action) => edit.setOrderNote(state, action),
    setChangeShippingAddress: (state, action) => edit.changeShippingAddress(state, action),
    updateShippingAddress: (state, action) => edit.updateShippingAddress(state, action),
    setChangePaymentMethod: (state, action) => edit.changePaymentMethod(state, action),
    setShippingRate: (state, action) => edit.changeShippingRate(state, action),
    setShippingPrice: (state, action) => edit.setShippingPrice(state, action),
    setOrderEditDiscounts: (state, action) => edit.setDiscounts(state, action),
    setShippingComplianceAndTax: (state, action) => edit.setShippingCompliance(state, action),
    setRemovePromoDiscount: (state) => edit.removePromoCode(state),
    setRemoveShippingDiscount: (state) => edit.removeShippingDiscount(state),
    setIsTaxRemoved: (state, action) => edit.setIsTaxRemoved(state, action),
    setIsAutoDiscountRemoved: (state, action) => edit.setIsAutoDiscountRemoved(state, action),
    resetShipping: (state) => edit.resetShippingTaxDiscount(state),
    setOrderNotes: (state, action) => edit.setOrderNotes(state, action),
    resetOrderState: () => orderAdapter.getInitialState(orderEditInitialState),
  },

  extraReducers: {
    [createEditOrder.fulfilled.toString()]: orderAdapter.setAll,
    [checkCompliance.fulfilled.toString()]: (state, action) =>
      edit.setShippingComplianceResponse(state, action),
    [getDiscountList.fulfilled.toString()]: (state, action) => {
      state.discountCodeList = action.payload
    },
  },
})

export const {
  setIsSubmitting,
  setEditOrder,
  setIsFetching,
  setAddNewItems,
  setOrderItems,
  addManyProducts,
  updateOrderItem,
  removeOrderItem,
  setIsDirty,
  setDeletedItems,
  resetOrderState,
  setChangeShippingAddress,
  setOrderNote,
  setOrderDeliveryMethod,
  setRemoveNewItem,
  setOrderSubtotal,
  setShippingRate,
  setShippingComplianceAndTax,
  setCompliantFetch,
  resetShipping,
  setRemovePromoDiscount,
  setRemoveShippingDiscount,
  setErrors,
  addOrderItem,
  setChangePaymentMethod,
  setIsValidatingDiscount,
  setOrderErrors,
  setIsProductsEditMode,
  setShippingPrice,
  setOrderNotes,
  setOrderEditDiscounts,
  setIsTaxRemoved,
  setIsAutoDiscountRemoved,
  updateShippingAddress,
  setIsCustomShipping,
  updateManyOrderItems,
  setIsDirtyDiscount,
  setIsDirtyShipping,
  setIsShippingAddressSame,
} = orderEditSlice.actions

export default orderEditSlice.reducer

// View Order
export const selectViewOrder = ({orders}: RootState): ViewOrder => orders.orderEdit.viewOrder

export const selectIsCRVEnabled = ({orders}: RootState): boolean =>
  orders.orderEdit.viewOrder.isCrvEnabled

export const selectViewOrderDeliveryMethod = ({orders}: RootState): string | any =>
  orders.orderEdit.viewOrder.orderDeliveryMethod
export const selectViewOrderCreatedAt = ({orders}: RootState): string | any =>
  orders.orderEdit.viewOrder.createdAt
export const selectViewOrderNumber = ({orders}: RootState): any =>
  orders.orderEdit.viewOrder.orderNumber

export const selectOrderCustomerId = ({orders}: RootState): string =>
  orders.orderEdit.viewOrder.customerId
export const selectViewOrderNote = ({orders}: RootState): string => orders.orderEdit.viewOrder.note

export const selectViewOrderItems = ({orders}: RootState): any[] =>
  orders.orderEdit.viewOrder.orderItems

export const selectViewOrderNotes = ({orders}: RootState): any[] => orders.orderEdit.viewOrder.notes

export const selectViewOrderType = ({orders}: RootState): string | any =>
  orders.orderEdit.viewOrder.orderType

// Edit Order
export const selectIsProductsEditMode = ({orders}: RootState): boolean =>
  orders.orderEdit.isProductsEditMode

export const selectEditOrder = ({orders}: RootState): Order => orders.orderEdit.order
export const selectEditOrderId = ({orders}: RootState): string => orders.orderEdit.order.orderEditId
export const selectOrderDeliveryMethod = ({orders}: RootState): string | any =>
  orders.orderEdit.order.orderDeliveryMethod
export const selectOrderShippingAddress = ({orders}: RootState): any =>
  orders.orderEdit.order.shippingAddress
export const selectOrderPaymentMethod = ({orders}: RootState): any =>
  orders.orderEdit.order.paymentMethod
export const selectOrderBillingAddress = ({orders}: RootState): any =>
  orders.orderEdit.order.billingAddress
export const selectOrderNote = ({orders}: RootState): string => orders.orderEdit.order.note
export const selectOrderErrors = ({orders}: RootState): any[] => orders.orderEdit.errors
export const selectOrderProcessErrors = ({orders}: RootState): any[] =>
  orders.orderEdit.orderProcessErrors
export const selectDiscountList = ({orders}: RootState): any[] => orders.orderEdit.discountCodeList

export const selectOrderEditIsSubmitting = ({orders}: RootState): boolean =>
  orders.orderEdit.isSubmitting

export const selectOrderEditIsFetching = ({orders}: RootState): boolean =>
  orders.orderEdit.isFetching
export const selectIsDirty = ({orders}: RootState): boolean => orders.orderEdit.isDirty
export const selectCompliance = ({orders}: RootState): any => orders.orderEdit.compliance
export const selectIsComplianceChecked = ({orders}: RootState): boolean =>
  orders.orderEdit.compliance.isComplianceChecked
export const selectIsCompliantFetch = ({orders}: RootState): boolean =>
  orders.orderEdit.isCompliantFetching
export const selectDeletedItems = ({orders}: RootState): any[] => orders.orderEdit.deletedItems
export const selectNewOrderItems = ({orders}: RootState): any[] => orders.orderEdit.newItems
export const selectIsValidateDiscount = ({orders}: RootState): boolean =>
  orders.orderEdit.isValidateDiscount
export const selectIsValidatingDiscount = ({orders}: RootState): boolean =>
  orders.orderEdit.isValidatingDiscount
export const selectOrderDiscounts = ({orders}: RootState): any => orders.orderEdit.discounts
export const selectSubtotalPrice = ({orders}: RootState): any =>
  orders.orderEdit.order.subtotalPrice
export const selectIsTaxRemoved = ({orders}: RootState): boolean =>
  orders.orderEdit.order.isTaxRemoved
export const selectIsAutoDiscountRemoved = ({orders}: RootState): boolean =>
  orders.orderEdit.order.isAutoDiscountRemoved

export const selectIsWineMackerChoiceDiscountDisabled = ({orders}: RootState): boolean =>
  orders.orderEdit.order.isWineMackerChoiceDiscountDisabled

export const selectViewOrderHistory = ({orders}: RootState): any[] =>
  orders.orderEdit.viewOrder.orderHistory
export const selectShippingIsCustom = ({orders}: RootState): boolean =>
  orders.orderEdit.isCustomRate

export const selectIsShippingAddressSame = ({orders}: RootState): boolean =>
  orders.orderEdit.isShippingAddressSame

const mapToLineItem = (items: any[]) => {
  return items.map(({variantId, productId, quantity, price, discountPrice}) => {
    const finalPrice = parseFloat(discountPrice) > 0 ? discountPrice : price
    return {
      variantId,
      productId,
      quantity: parseInt(quantity),
      price: parseFloat(finalPrice),
    }
  })
}

const filterNewItems = (items: any[]) => filter(items, (item: any) => !item.id)

const mapToUpdateLineItemsIds = (items: any[], updatedItems: any[]) => {
  return filterNewItems(items).map((lineItem) => {
    const item = find(updatedItems, (item) => item.variantId === lineItem.variantId)
    return {
      id: lineItem.variantId,
      changes: {
        id: item?.id || lineItem.id,
      },
    }
  })
}
